import { useQuery } from '@tanstack/react-query'

import { type AppTimezonePreference } from '@sparkai/utils'

import { apiClient } from '../apiClient'
import {
  getOperatorPerformanceQueryTimezonePart,
  getOperatorPerformanceRequestHeaders,
} from './operatorPerformanceTimezone'

export const OPERATOR_PROGRAM_INSTANCES_KEY = 'operator-program-instances'

export type OperatorProgramInstanceSource = {
  id: string
  name: string | null
  displayName: string | null
  iconType: string | null
  color: string | null
  isActive: boolean
}

export type OperatorProgramInstance = {
  id: string
  name: string | null
  displayName: string | null
  iconType: string | null
  color: string | null
  programGroup: { id: string; name: string; msDisplayName: string | null }
  liveProgram: OperatorProgramInstanceSource
  recyclerProgram: OperatorProgramInstanceSource | null
}

export type GetOperatorProgramInstancesRequest = {
  operatorId?: string
  startDate: string
  endDate: string
  timezonePreference?: AppTimezonePreference
}

export function getOperatorProgramInstancesQueryKey(params: GetOperatorProgramInstancesRequest) {
  const timezoneKey = getOperatorPerformanceQueryTimezonePart(params.timezonePreference)
  return [
    OPERATOR_PROGRAM_INSTANCES_KEY,
    params.operatorId,
    params.startDate,
    params.endDate,
    ...(timezoneKey ? [timezoneKey] : []),
  ] as const
}

export function getOperatorProgramInstancesDiscoveryIdentity(params: GetOperatorProgramInstancesRequest) {
  return JSON.stringify(getOperatorProgramInstancesQueryKey(params))
}

export async function fetchOperatorProgramInstances(params: GetOperatorProgramInstancesRequest) {
  const headers = getOperatorPerformanceRequestHeaders(params.timezonePreference)
  const { data } = await apiClient.get<{ instances: OperatorProgramInstance[] }>(
    `/int/v3/operators/${params.operatorId}/program-instances`,
    {
      params: { startDate: params.startDate, endDate: params.endDate },
      ...(headers ? { headers } : {}),
    }
  )
  return data
}

export function useGetOperatorProgramInstances(params: GetOperatorProgramInstancesRequest) {
  return useQuery({
    queryKey: getOperatorProgramInstancesQueryKey(params),
    queryFn: () => fetchOperatorProgramInstances(params),
    enabled: Boolean(params.operatorId),
    refetchOnWindowFocus: false,
  })
}
